import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { radii } from '@/theme';
import { useModeTokens } from './useModeTokens';

export type ActionButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ActionButtonSize = 'sm' | 'md' | 'lg' | 'xl';

interface ActionButtonProps {
  children?: React.ReactNode;
  variant?: ActionButtonVariant;
  size?: ActionButtonSize;
  /** Suppress the physical elevation under a primary button. */
  glow?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  /** Render as an anchor instead of a button. */
  href?: string;
  /**
   * Render as something else entirely — typically react-router's `Link`, so a
   * CTA navigates without a full page load. Extra props (`to`, …) pass through.
   */
  component?: React.ElementType;
  /** Leading element — an icon or a spinner. */
  startIcon?: React.ReactNode;
  'aria-label'?: string;
  /** Pin to one mode instead of following the theme. */
  light?: boolean;
  sx?: SxProps<Theme>;
  /** Forwarded to whatever `component` renders — e.g. `to` for a `Link`. */
  [key: string]: unknown;
}

const SIZES: Record<ActionButtonSize, { padding: string; fontSize: number }> = {
  sm: { padding: '4px 10px', fontSize: 12 },
  md: { padding: '6px 13px', fontSize: 13 },
  lg: { padding: '7px 15px', fontSize: 13 },
  xl: { padding: '9px 18px', fontSize: 13.5 },
};

/**
 * The design's three button weights.
 *
 * Built on a plain `button` element rather than MUI's Button because the
 * physical button edge, near-black `onAccent` label and compact radius fight MUI's
 * defaults more than they benefit from them.
 *
 * The font is read from the theme rather than pinned to a family. Under the
 * signal gallery's theme that resolves to Manrope, and under the app theme to
 * Inter — one component, the right voice on both surfaces.
 */
export const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  glow = true,
  fullWidth = false,
  disabled = false,
  onClick,
  type = 'button',
  href,
  component,
  startIcon,
  'aria-label': ariaLabel,
  light,
  sx,
  ...rest
}) => {
  const t = useModeTokens(light);
  const { padding, fontSize } = SIZES[size];

  const variants: Record<
    ActionButtonVariant,
    { base: Record<string, unknown>; hover: Record<string, unknown> }
  > = {
    primary: {
      base: {
        background: t.colors.accentFill,
        color: t.colors.onAccent,
        border: `1px solid ${t.colors.accentFillHover}`,
      },
      hover: { background: t.colors.accentFillHover },
    },
    secondary: {
      base: {
        background: t.surfaces.fillActive,
        color: t.text.primary,
        border: `1px solid ${t.borders.control}`,
      },
      hover: { background: t.surfaces.fillHeavy },
    },
    ghost: {
      base: {
        background: 'transparent',
        color: t.text.secondary,
        border: 0,
      },
      hover: { color: t.text.primary, background: t.surfaces.fillHover },
    },
  };

  const { base, hover } = variants[variant];

  return (
    <Box
      component={component ?? (href ? 'a' : 'button')}
      {...(component || href
        ? {
            ...(href ? { href } : null),
            ...(disabled ? { 'aria-disabled': true } : null),
          }
        : { type, disabled })}
      {...rest}
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        cursor: disabled ? 'default' : 'pointer',
        textDecoration: 'none',
        fontFamily: 'inherit',
        fontWeight: 600,
        fontSize: `${fontSize}px`,
        lineHeight: 1.2,
        padding,
        borderRadius: `${radii.md}px`,
        width: fullWidth ? '100%' : 'auto',
        flex: 'none',
        whiteSpace: 'nowrap',
        transition:
          'background 120ms ease, color 120ms ease, box-shadow 90ms ease, transform 90ms ease',
        ...base,
        ...(variant !== 'ghost' && glow
          ? {
              boxShadow:
                variant === 'primary' ? t.shadows.accent : t.shadows.button,
            }
          : null),
        ...(disabled ? { opacity: 0.55, pointerEvents: 'none' } : null),
        '&:hover': disabled ? undefined : hover,
        '&:active': disabled
          ? undefined
          : {
              transform: 'translateY(1px)',
              boxShadow: t.shadows.buttonPressed,
            },
        ...sx,
      }}
    >
      {startIcon}
      {children}
    </Box>
  );
};

export default ActionButton;
